import {
  createCodeableConcept,
  createIdentifier,
  type DeviceCode,
  getCodes,
  IdentifierCode,
  isDevicePatch,
  type WithId,
} from '@quromedical/fhir-common';
import { first } from '@quromedical/utils';
import type { DeviceDeviceName, Device as FhirDevice } from 'fhir/r4';

import { type CreateDeviceRequest, type Device, DeviceSchema } from './schema';

const makeStatusInactive = <T extends FhirDevice>(device: T): T => {
  const status = 'inactive';
  const result: T = {
    ...device,
    status,
  };

  return result;
};

export const makePatchDevicesInactive = <T extends FhirDevice>(
  devices: T[],
): T[] => {
  // We only have to update the status of patches to inactive.
  const patchDevices = devices.filter((device) => isDevicePatch(device));

  const inactivePatchDevices = patchDevices.map(makeStatusInactive);
  return inactivePatchDevices;
};

export interface DeviceNameWithId {
  id?: string;
  name?: string;
}

export const mapDeviceToNameAndId = (device: FhirDevice): DeviceNameWithId => ({
  id: device.id,
  name: (device.deviceName && device.deviceName[0].name) || undefined,
});

const mapUserFriendlyNameToDeviceName = (
  userFriendlyName: string,
): DeviceDeviceName[] => [
  {
    name: userFriendlyName,
    type: 'user-friendly-name',
  },
];

const mapDeviceNameTopUserFriendlyName = (
  deviceName?: DeviceDeviceName[],
): string | undefined => first(deviceName)?.name;

export const mapDeviceCreateModelToFhir = (
  device: CreateDeviceRequest,
): Omit<FhirDevice, 'id'> => ({
  resourceType: 'Device',
  identifier: [createIdentifier(IdentifierCode.deviceType, device.type)],
  deviceName: mapUserFriendlyNameToDeviceName(device.name),
  status: device.status,
  serialNumber: device.serialNumber,
  type: createCodeableConcept(device.type),
});

export const mapDeviceToFhir = (device: Device): WithId<FhirDevice> => {
  const base = mapDeviceCreateModelToFhir(device);

  return {
    id: device.id,
    ...base,
  };
};

export const mapFhirToDevice = (
  device: WithId<FhirDevice>,
): Device | undefined => {
  const item = {
    id: device.id,
    name: mapDeviceNameTopUserFriendlyName(device.deviceName),
    status: device.status,
    serialNumber: device.serialNumber,
    type: first(getCodes<DeviceCode>(device.type)),
  };

  const parsed = DeviceSchema.safeParse(item);

  if (!parsed.success) {
    return undefined;
  }

  return parsed.data;
};
